import EXIF from 'exif-js'
import FileSaver from 'file-saver'

export function loadImage(imgSrc) {
  const image = new window.Image()

  return new Promise((resolve) => {
    image.onload = () => {
      resolve(image)
    }
    image.src = imgSrc
  })
}

export function loadImageFile(file) {
  const reader = new window.FileReader()

  return new Promise((resolve) => {
    reader.onload = (event) => {
      resolve(event.target.result)
    }
    reader.readAsDataURL(file)
  })
}

export function loadVideoFile(file) {
  const reader = new window.FileReader()

  return new Promise((resolve) => {
    reader.onload = (event) => {
      resolve(event.target.result)
    }

    reader.readAsArrayBuffer(file)
  })
}

export async function getVideoInputUrl(file) {
  if (!file.type.match(/video.*/)) {
    return null
  }

  try {
    const fileData = await loadVideoFile(file)
    // eslint-disable-next-line no-undef
    const blob = new Blob([fileData], { type: file.type })
    const url = URL.createObjectURL(blob)

    // revoke this later when unmounting with URL.revokeObjectURL(url)
    return url
  } catch (error) {
    console.error(error)
    return new Promise.reject('File could not be loaded')
  }
}

export function getOrientation(file) {
  return new Promise((resolve) => {
    EXIF.getData(file, function orientation() {
      resolve(EXIF.getAllTags(this).Orientation)
    })
  })
}

export async function resizeImage(
  image,
  file,
  maxWidth = 160,
  maxHeight = 160,
) {
  const canvas = document.createElement('canvas')

  const imageWidth = image.width
  const imageHeight = image.height
  let targetWidth = imageWidth
  let targetHeight = imageHeight

  if (imageWidth > imageHeight && imageHeight > maxHeight) {
    // if the image is too wide, resize the height and width accordingly
    targetWidth *= maxWidth / imageHeight // set the width to be in the same ratio after the height resizes
    targetHeight = maxHeight // set the target heigth to be the max height
  } else if (imageHeight > imageWidth && imageWidth > maxWidth) {
    // if the image is too tall, resize the height and width accordingly
    targetHeight *= maxHeight / imageWidth // set the height to be in the same ratio after the width resizes
    targetWidth = maxWidth // set the target width to be the max width
  }

  // Draw image on canvas and resize
  canvas.width = targetWidth
  canvas.height = targetHeight

  // rotate image if user took the photo in landscape
  const orientation = await getOrientation(file)

  if ([5, 6, 7, 8].indexOf(orientation) > -1) {
    canvas.width = targetHeight
    canvas.height = targetWidth
  } else {
    canvas.width = targetWidth
    canvas.height = targetHeight
  }

  const ctx = canvas.getContext('2d')

  switch (orientation) {
    case 2:
      ctx.transform(-1, 0, 0, 1, targetWidth, 0)
      break
    case 3:
      ctx.transform(-1, 0, 0, -1, targetWidth, targetHeight)
      break
    case 4:
      ctx.transform(1, 0, 0, -1, 0, targetHeight)
      break
    case 5:
      ctx.transform(0, 1, 1, 0, 0, 0)
      break
    case 6:
      ctx.transform(0, 1, -1, 0, targetHeight, 0)
      break
    case 7:
      ctx.transform(0, -1, -1, 0, targetHeight, targetWidth)
      break
    case 8:
      ctx.transform(0, -1, 1, 0, 0, targetWidth)
      break
    default:
      ctx.transform(1, 0, 0, 1, 0, 0)
  }

  ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

  // return the data url
  return canvas.toDataURL('image/png')
}

export function getVideoThumbnail(fileData, file) {
  // eslint-disable-next-line no-undef
  const blob = new Blob([fileData], { type: file.type })
  const url = URL.createObjectURL(blob)
  const video = document.createElement('video')

  const snapImage = () => {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
    const imgElement = new window.Image()

    const imgSrc = canvas.toDataURL()
    const success = imgSrc.length > 100000

    if (!success) {
      return null
    }

    URL.revokeObjectURL(url)
    imgElement.src = imgSrc
    return imgElement
  }

  return new Promise((resolve) => {
    const timeupdate = () => {
      const image = snapImage()

      if (image) {
        video.removeEventListener('timeupdate', timeupdate)
        video.pause()
        resolve(image)
      }
    }

    video.addEventListener('loadeddata', () => {
      const image = snapImage()

      if (image) {
        video.removeEventListener('timeupdate', timeupdate)
        resolve(image)
      }
    })

    video.addEventListener('timeupdate', timeupdate)
    video.preload = 'metadata'
    video.src = url
    // Load video in safari / ie11
    video.muted = true
    video.playsInline = true
    video.play()
  })
}

export async function getResizedVideoThumbnail(file) {
  if (!file.type.match(/video.*/)) {
    return null
  }

  try {
    const fileData = await loadVideoFile(file)
    const image = await getVideoThumbnail(fileData, file)

    return resizeImage(image, file)
  } catch (error) {
    console.error('Video could not be converted to a data url')
    console.error(error)
    return new Promise.reject('File could not be loaded')
  }
}

export async function getResizedImageFromFile(file) {
  if (!file.type.match(/image.*/)) {
    return null
  }

  try {
    const fileData = await loadImageFile(file)
    const image = await loadImage(fileData)

    return resizeImage(image, file)
  } catch (error) {
    console.error('Image could not be converted to a data url')
    console.error(error)
    return new Promise.reject('File could not be loaded')
  }
}

export function getNumUploadableFiles(files) {
  return files.reduce((acc, file) => {
    if (file.file) {
      return acc + 1
    }

    return acc
  }, 0)
}

export function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = window.atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  // eslint-disable-next-line
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  // eslint-disable-next-line no-undef
  return new File([u8arr], filename, {type: mime})
}

export function downloadDataAsCsv(data, fileName) {
  // eslint-disable-next-line no-undef
  const blob = new Blob([data], {type: 'text/plain;charset=utf-8'})
  FileSaver.saveAs(blob, `${fileName}.csv`)
}



// WEBPACK FOOTER //
// ./src/utils/files.js